const SCOPE_STORAGE_KEY = 'tenantscore_property_scope_v1';

function safeParseJson(raw, fallback) {
  try {
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch (_) {
    return fallback;
  }
}

function normalizeScope(scope) {
  const s = String(scope || '').trim().toLowerCase();
  if (s === 'bnb' || s === 'bnb-host' || s === 'airbnb' || s === 'airbnb-host') return 'bnb';
  return 'landlord';
}

function loadScopeMap() {
  if (typeof window === 'undefined') return {};
  const raw = window.localStorage.getItem(SCOPE_STORAGE_KEY);
  const parsed = safeParseJson(raw, {});
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  return parsed;
}

function saveScopeMap(map) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(map));
}

export function setPropertyScope(propertyId, scope) {
  const id = propertyId != null ? String(propertyId).trim() : '';
  if (!id) return;
  const normalized = normalizeScope(scope);
  const map = loadScopeMap();
  map[id] = normalized;
  saveScopeMap(map);
}

export function getPropertyScope(propertyId) {
  const id = propertyId != null ? String(propertyId).trim() : '';
  if (!id) return null;
  const map = loadScopeMap();
  const scope = map[id];
  if (!scope) return null;
  return normalizeScope(scope);
}

export function filterPropertiesByScope(properties, scope, options = {}) {
  const normalized = normalizeScope(scope);
  const {
    // If a property has no explicit scope, decide whether to include it.
    includeUnscoped = normalized === 'landlord',
    // If true, attempt to infer scope from units for unscoped properties.
    inferFromUnits = true,
  } = options;

  // Treat only positive numeric values as "real" pricing.
  // Backends sometimes return empty strings or 0 for the other pricing type,
  // which would previously be considered "non-null" and cause mixed-scoping.
  const parsePositiveNumber = (value) => {
    if (value == null) return null;
    const trimmed = typeof value === 'string' ? value.trim() : value;
    if (trimmed === '') return null;
    const num = Number(trimmed);
    if (!Number.isFinite(num)) return null;
    return num > 0 ? num : null;
  };

  const inferUnitScopeFromText = (unit) => {
    const tokens = [
      unit?.unitSubCategoryName,
      unit?.unitSubcategoryName,
      unit?.subcategoryName,
      unit?.subCategoryName,
      unit?.unitCategoryName,
      unit?.categoryName,
      unit?.typeName,
      unit?.unitTypeName,
    ]
      .map((v) => String(v || '').trim().toLowerCase())
      .filter(Boolean)
      .join(' ');

    if (!tokens) return null;
    if (tokens.includes('airbnb') || tokens.includes('bnb')) return 'bnb';
    if (tokens.includes('rental') || tokens.includes('rent')) return 'landlord';
    return null;
  };

  const list = Array.isArray(properties) ? properties : [];
  return list.filter((p) => {
    const propertyId = p?.id;
    const explicit = getPropertyScope(propertyId);
    if (explicit) return explicit === normalized;

    if (inferFromUnits) {
      const units = Array.isArray(p?.units) ? p.units : [];
      const hasBnbUnit = units.some((u) => parsePositiveNumber(u?.pricePerNight) != null);
      const hasRentalUnit = units.some((u) => parsePositiveNumber(u?.monthlyRent) != null);
      const hasBnbCheckout = units.some((u) => String(u?.checkoutTime || '').trim() !== '');
      const hasRentalDeposit = units.some((u) => parsePositiveNumber(u?.depositAmount) != null);
      const hasBnbText = units.some((u) => inferUnitScopeFromText(u) === 'bnb');
      const hasRentalText = units.some((u) => inferUnitScopeFromText(u) === 'landlord');

      const inferredBnb = hasBnbUnit || hasBnbCheckout || hasBnbText;
      const inferredRental = hasRentalUnit || hasRentalDeposit || hasRentalText;

      if (inferredBnb && !inferredRental) return normalized === 'bnb';
      if (inferredRental && !inferredBnb) return normalized === 'landlord';
      // Mixed or empty units -> fallthrough to includeUnscoped
    }

    return !!includeUnscoped;
  });
}

